import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { StatsCardSummary, EmptyState } from "@/components/common";
import { NotificationsSection } from "@/features/admin/home/components/NotificationsSection";
import { ActionRequiredCard } from "./components";
import { useAuth } from "@/context/AuthContext";
import { useNotificationContext } from "@/context/NotificationContext";
import { useAlert } from "@/hooks/useAlert";
import { quotesService } from "@/api/services/quotes.service";
import { jobsService, } from "@/api/services/jobs.service";
import { invoicesService } from "@/api/services/invoices.service";
import { notificationsService } from "@/api/services/notifications.service";
import { matchesQuoteStatus } from "@/utils/quoteHelpers";
import { getUserDisplayName } from "@/utils/formatDisplayName";
import styles from "./ClientHome.module.css";
export const ClientHome = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { confirm } = useAlert();
    const { latestNotification, clearLatestNotification } = useNotificationContext();
    // Data states
    const [quotes, setQuotes] = useState([]);
    const [jobCounts, setJobCounts] = useState(null);
    const [unpaidInvoices, setUnpaidInvoices] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [notificationsTotalCount, setNotificationsTotalCount] = useState(0);
    const [notificationsLoadingMore, setNotificationsLoadingMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Fetch all data for the dashboard
    const fetchDashboardData = useCallback(async () => {
        if (!user?.id)
            return;
        try {
            setLoading(true);
            setError(null);
            const [quotesRes, jobCountsRes, invoicesRes] = await Promise.all([
                quotesService.getQuotesByContact(user.id),
                jobsService.getJobCountsByContact(user.id),
                invoicesService.getClientInvoices(false), // Get unpaid invoices
            ]);
            setQuotes(quotesRes.data);
            setJobCounts(jobCountsRes.data);
            setUnpaidInvoices(invoicesRes.data.invoices ?? []);
        }
        catch (err) {
            console.error("Error fetching dashboard data:", err);
            setError("Failed to load dashboard data.");
        }
        finally {
            setLoading(false);
        }
    }, [user?.id]);
    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);
    const NOTIFICATIONS_PAGE_SIZE = 20;
    const fetchNotifications = useCallback(async () => {
        try {
            const res = await notificationsService.getMyNotifications({
                unreadOnly: true,
                limit: NOTIFICATIONS_PAGE_SIZE,
                offset: 0,
            });
            setNotifications(res.data.data ?? []);
            setNotificationsTotalCount(res.data.totalCount ?? 0);
        }
        catch {
            setNotifications([]);
            setNotificationsTotalCount(0);
        }
    }, []);
    const handleLoadMoreNotifications = useCallback(async () => {
        if (notificationsLoadingMore ||
            notifications.length >= notificationsTotalCount)
            return;
        setNotificationsLoadingMore(true);
        try {
            const res = await notificationsService.getMyNotifications({
                unreadOnly: true,
                limit: NOTIFICATIONS_PAGE_SIZE,
                offset: notifications.length,
            });
            const newItems = res.data.data ?? [];
            setNotifications((prev) => [...prev, ...newItems]);
        }
        finally {
            setNotificationsLoadingMore(false);
        }
    }, [
        notificationsLoadingMore,
        notifications.length,
        notificationsTotalCount,
    ]);
    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);
    useEffect(() => {
        if (!latestNotification)
            return;
        setNotifications((prev) => [latestNotification, ...prev]);
        clearLatestNotification();
    }, [latestNotification, clearLatestNotification]);
    // Calculate quote counts by status
    const quoteCounts = useMemo(() => {
        const counts = {
            requested: 0,
            forApproval: 0,
            accepted: 0,
            declined: 0,
        };
        quotes.forEach((quote) => {
            if (matchesQuoteStatus(quote.status, "inprogress") &&
                quote.quoteRequestId) {
                counts.requested++;
            }
            else if (matchesQuoteStatus(quote.status, "pending")) {
                counts.forApproval++;
            }
            else if (matchesQuoteStatus(quote.status, "approved")) {
                counts.accepted++;
            }
            else if (matchesQuoteStatus(quote.status, "declined")) {
                counts.declined++;
            }
        });
        return counts;
    }, [quotes]);
    // Quote status data for cards
    const quoteStatusData = [
        { label: "Requested Quotes", value: String(quoteCounts.requested), sectionId: "requested" },
        { label: "Quotes For Approval", value: String(quoteCounts.forApproval), sectionId: "for-approval", circleVariant: "yellow" },
        { label: "Accepted Quotes", value: String(quoteCounts.accepted), sectionId: "accepted" },
        { label: "Declined Quotes", value: String(quoteCounts.declined), sectionId: "declined" },
    ];
    // Job status data for cards
    const jobStatusData = [
        { label: "In Progress", value: String(jobCounts?.in_progress || 0), sectionId: "in-progress" },
        { label: "Completed", value: String(jobCounts?.completed || 0), sectionId: "completed" },
        { label: "On Hold", value: String(jobCounts?.on_hold || 0), sectionId: "on-hold" },
    ];
    const handlePayInvoice = (_invoiceNumber) => {
        navigate("/client/invoices");
    };
    const handleNotificationClick = useCallback(async (notification) => {
        const confirmed = await confirm("Are you sure you want to close this notification as it will disappear?", "Confirm");
        if (!confirmed)
            return;
        const id = notification.id;
        setNotifications((prev) => prev.filter((n) => n.id !== id));
        try {
            await notificationsService.markAsRead(id, {
                actionedByName: getUserDisplayName(user, "User"),
            });
        }
        catch {
            fetchNotifications();
        }
    }, [confirm, fetchNotifications, user]);
    if (loading) {
        return (_jsx("div", { className: styles.pageContent, children: _jsx("div", { className: styles.loadingState, children: _jsx("p", { children: "Loading dashboard..." }) }) }));
    }
    if (error) {
        return (_jsx("div", { className: styles.pageContent, children: _jsxs("div", { className: styles.errorState, children: [_jsx("p", { children: error }), _jsx("button", { onClick: fetchDashboardData, children: "Try Again" })] }) }));
    }
    return (_jsx("div", { className: styles.pageContent, children: _jsxs("div", { className: styles.topSection, children: [_jsxs("div", { className: styles.mainContent, children: [_jsxs("div", { className: styles.mainGrid, children: [_jsxs("div", { className: `${styles.wrapper} ${styles.quoteStatusWrapper}`, children: [_jsxs("div", { className: styles.quoteStatusHeader, children: [_jsx("h2", { className: styles.sectionTitle, children: "Quote Status" }), _jsx("button", { type: "button", className: styles.quoteRequestButton, onClick: () => navigate("/client/quotes/new"), children: "REQUEST QUOTE" })] }), _jsx("div", { className: styles.section, children: quoteStatusData.every((quote) => quote.value === "0") ? (_jsx(EmptyState, { image: "/images/empty-state-quote.png", heading: "No quote activity", text: "Your quote requests and updates will appear here." })) : (_jsx("div", { className: `${styles.quotesList} ${styles.quoteStatusGrid}`, children: quoteStatusData.map((quote) => (_jsx(StatsCardSummary, { title: quote.label, total: quote.value, onClick: () => navigate(`/client/quotes#${quote.sectionId}`), circleVariant: quote.circleVariant }, quote.label))) })) })] }), _jsxs("div", { className: styles.wrapper, children: [_jsx("h2", { className: unpaidInvoices.length > 0
                                                ? `${styles.sectionTitle} ${styles.sectionTitleClickable}`
                                                : styles.sectionTitle, ...(unpaidInvoices.length > 0 && {
                                                role: "button",
                                                tabIndex: 0,
                                                onClick: () => navigate("/client/invoices#for-payment"),
                                                onKeyDown: (e) => {
                                                    if (e.key === "Enter" || e.key === " ") {
                                                        e.preventDefault();
                                                        navigate("/client/invoices#for-payment");
                                                    }
                                                },
                                            }), children: "Invoices" }), _jsx("div", { className: styles.section, children: unpaidInvoices.length === 0 ? (_jsx(EmptyState, { image: "/images/empty-state-action.png", heading: "No invoices", text: "You have no unpaid invoices." })) : (_jsx("div", { className: `${styles.quotesList} ${styles.actionRequiredList}`, children: unpaidInvoices.slice(0, 4).map((invoice) => (_jsx(ActionRequiredCard, { invoiceNumber: `Invoice ${invoice.invoiceNumber}`, address: invoice.address || "", location: invoice.suburb || "", onPayInvoice: handlePayInvoice }, invoice.id))) })) })] })] }), _jsx("div", { className: styles.statsGrid, children: _jsxs("div", { className: `${styles.wrapper} ${styles.jobStatusWrapper}`, children: [_jsx("h2", { className: styles.sectionTitle, children: "Job Status" }), _jsx("div", { className: styles.section, children: jobStatusData.every((job) => job.value === "0") ? (_jsx(EmptyState, { image: "/images/empty-state-job.png", heading: "No active jobs", text: "Job progress and status updates will be shown here." })) : (_jsx("div", { className: `${styles.quotesList} ${styles.jobStatusGrid}`, children: jobStatusData.map((job) => (_jsx(StatsCardSummary, { title: job.label, total: job.value, onClick: () => navigate(`/client/jobs#${job.sectionId}`) }, job.label))) })) })] }) })] }), _jsx("div", { className: styles.notificationsColumn, children: _jsx(NotificationsSection, { notifications: notifications, onNotificationClick: handleNotificationClick, fromClientHome: true, onLoadMore: handleLoadMoreNotifications, hasMore: notifications.length < notificationsTotalCount, loadingMore: notificationsLoadingMore }) })] }) }));
};
